import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { ShoppingCart, Calendar, Eye, User } from 'lucide-react';
import type { NurseryOrder, NurseryOrderStatus } from '../../types/nursery';

interface OrderCardProps {
  order: NurseryOrder;
}

const ORDER_STATUS_CONFIG: Record<NurseryOrderStatus, { label: string; dot: string; badge: string }> = {
  draft:               { label: 'Draft',              dot: 'bg-neutral-400',  badge: 'bg-neutral-100 text-neutral-700'  },
  confirmed:           { label: 'Confirmed',          dot: 'bg-blue-500',     badge: 'bg-blue-100 text-blue-700'        },
  allocated:           { label: 'Allocated',          dot: 'bg-indigo-500',   badge: 'bg-indigo-100 text-indigo-700'    },
  partially_allocated: { label: 'Partial Allocation', dot: 'bg-amber-500',    badge: 'bg-amber-100 text-amber-700'      },
  picking:             { label: 'Picking',            dot: 'bg-orange-500',   badge: 'bg-orange-100 text-orange-700'    },
  picked:              { label: 'Picked',             dot: 'bg-teal-500',     badge: 'bg-teal-100 text-teal-700'        },
  shipped:             { label: 'Shipped',            dot: 'bg-cyan-500',     badge: 'bg-cyan-100 text-cyan-700'        },
  invoiced:            { label: 'Invoiced',           dot: 'bg-purple-500',   badge: 'bg-purple-100 text-purple-700'    },
  completed:           { label: 'Completed',          dot: 'bg-green-500',    badge: 'bg-green-100 text-green-700'      },
  cancelled:           { label: 'Cancelled',          dot: 'bg-red-400',      badge: 'bg-red-100 text-red-700'          },
};

function OrderCard({ order }: OrderCardProps) {
  const navigate = useNavigate();
  const cfg = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.draft;

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-neutral-400 font-mono">{order.order_number}</span>
              <span className={`inline-flex items-center gap-1.5 rounded-full font-medium px-2 py-0.5 text-xs ${cfg.badge}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
              </span>
            </div>
            {order.customer_name && (
              <h3 className="text-base font-semibold text-neutral-900 truncate">{order.customer_name}</h3>
            )}
            {order.parent_order_number && (
              <p className="text-xs text-amber-700 break-words">Backorder of {order.parent_order_number}</p>
            )}
          </div>
          <div className="flex-shrink-0 ml-3">
            <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-neutral-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-neutral-900">{order.total_trees}</p>
            <p className="text-xs text-neutral-500">Trees</p>
          </div>
          <div className="bg-neutral-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-neutral-900">
              ${Number(order.total_value).toFixed(2)}
            </p>
            <p className="text-xs text-neutral-500">Value</p>
          </div>
        </div>

        {/* Date & customer */}
        <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {order.order_date}
          </div>
          {order.customer_name && (
            <div className="flex items-center gap-1 truncate">
              <User className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{order.customer_name}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/nursery/orders/${order.id}`)}
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            View Order
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default OrderCard;
